import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ChevronLeft } from 'lucide-react'
import { initKioskKey, getKioskKey, kioskFetch, KioskAuthError } from '@/lib/kiosk'
import { useReloadOnNewBuild } from '@/lib/kioskReload'
import { C, applyKioskTheme, readStoredMode, type KioskMode } from '@/components/kiosk/kioskTheme'
import { StatusStrip } from '@/components/kiosk/StatusStrip'
import { PowerTile, PowerDetail } from '@/components/kiosk/PowerTile'
import { GpuTile, GpuDetail } from '@/components/kiosk/GpuTile'
import { NetworkTile, NetworkDetail } from '@/components/kiosk/NetworkTile'
import { NodesTile, NodesDetail } from '@/components/kiosk/NodesTile'
import { StacksTile, StacksDetail } from '@/components/kiosk/StacksTile'
import { AlertNotifier, AlertsDetail } from '@/components/kiosk/AlertNotifier'
import { ScreenCare } from '@/components/kiosk/ScreenCare'
import { CLEAN_MS, orbitOffset } from '@/lib/kioskScreenCare'

// Flipped by the QueryCache onError on any KioskAuthError (401/403 — revoked/
// invalid key). KioskInner polls it to switch to the "Clé refusée" screen.
let authFailedExternal = false

const kioskQC = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof KioskAuthError) authFailedExternal = true
    },
    // Any successful query proves the key works again. Without this the flag
    // is one-way: a single 401 during an API redeploy would leave the wall
    // panel stuck on "Clé refusée" until someone physically reloads it.
    onSuccess: () => {
      authFailedExternal = false
    },
  }),
  defaultOptions: {
    queries: {
      retry: (_n, error) => !(error instanceof KioskAuthError),
      retryDelay: (a) => Math.min(1000 * 2 ** a, 30_000),
      staleTime: 5_000,
    },
  },
})

// The backdrop and grid colours come from the palette, so they are applied as
// inline styles rather than baked into this static stylesheet.
const KIOSK_CSS = `
.kmono{font-family:ui-monospace,SFMono-Regular,Menlo,'Segoe UI Mono',monospace;font-variant-numeric:tabular-nums;letter-spacing:-0.01em}
.kpanel{box-shadow:0 1px 0 rgba(255,255,255,.02) inset, 0 10px 30px -18px rgba(0,0,0,.9)}
[data-ktheme="light"] .kpanel{box-shadow:0 1px 0 rgba(255,255,255,.8) inset, 0 1px 2px rgba(22,27,36,.05), 0 14px 36px -22px rgba(22,27,36,.22)}
[data-ktheme="light"] .kcard{box-shadow:0 1px 2px rgba(22,27,36,.04), 0 10px 24px -18px rgba(22,27,36,.18)}
[data-ktheme="light"] .kgrid-tex{display:none}
.kgrid-tex{
  background-size:34px 34px;
  -webkit-mask-image:radial-gradient(120% 90% at 50% 40%,#000 40%,transparent 85%);
  mask-image:radial-gradient(120% 90% at 50% 40%,#000 40%,transparent 85%);
}
@keyframes kfade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
.kin{animation:kfade .5s cubic-bezier(.2,.7,.2,1) both}
`

type TileId = 'power' | 'gpu' | 'nodes' | 'network' | 'stacks' | 'alerts'

const DETAILS: Record<TileId, { title: string; Detail: ComponentType }> = {
  power: { title: 'Énergie', Detail: PowerDetail },
  gpu: { title: 'GPU — le parc', Detail: GpuDetail },
  nodes: { title: 'Nodes du cluster', Detail: NodesDetail },
  network: { title: 'Réseau & SFP', Detail: NetworkDetail },
  stacks: { title: 'Stacks du cluster', Detail: StacksDetail },
  alerts: { title: 'Alertes ouvertes', Detail: AlertsDetail },
}

function DetailShell({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="kroot absolute inset-0 z-10 flex flex-col" style={{ background: C.rootGradient }}>
      <div className="flex h-12 shrink-0 items-center gap-3 border-b px-4" style={{ borderColor: C.line }}>
        <button onClick={onClose} className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold"
          style={{ color: C.ink, background: C.panelHi }}>
          <ChevronLeft className="h-4 w-4" /> Retour
        </button>
        <span className="text-sm font-semibold uppercase tracking-[0.18em]" style={{ color: C.dim }}>{title}</span>
      </div>
      <div className="kin min-h-0 flex-1 p-4">{children}</div>
    </div>
  )
}

/** "Key rejected" screen that keeps probing so the panel can heal itself.
 *
 * The kiosk is a wall display nobody interacts with, so a dead end here means
 * a human has to walk over and reload the browser. A revoked key keeps failing
 * and we keep showing this screen; a transient 401 (API redeploy, proxy blip)
 * clears on the next successful probe.
 */
function KioskAuthFailed({ onRecovered }: { onRecovered: () => void }) {
  useEffect(() => {
    let cancelled = false
    const probe = async () => {
      try {
        await kioskFetch('/api/summary')
        if (!cancelled) {
          authFailedExternal = false
          onRecovered()
        }
      } catch {
        /* still refused — stay on this screen */
      }
    }
    const id = setInterval(() => void probe(), 15_000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [onRecovered])

  return (
    <div className="kroot flex h-screen w-screen flex-col items-center justify-center"
      style={{ color: C.ink, background: C.rootGradient }}>
      <p className="text-2xl font-bold">Clé refusée</p>
      <p className="mt-2 text-sm" style={{ color: C.dim }}>La clé kiosk a été révoquée ou est invalide.</p>
      <p className="mt-4 text-xs" style={{ color: C.dim }}>Nouvelle tentative toutes les 15 s…</p>
    </div>
  )
}

function KioskInner({ mode, onToggleTheme }: { mode: KioskMode; onToggleTheme: () => void }) {
  // Unattended wall display: pick up a new frontend build without a human
  // having to reboot the Pi.
  useReloadOnNewBuild()
  const [now, setNow] = useState('')
  const [zoom, setZoom] = useState<TileId | null>(null)
  const [authFailed, setAuthFailed] = useState(authFailedExternal)
  const [orbit, setOrbit] = useState(() => orbitOffset(Date.now()))
  const [careUntil, setCareUntil] = useState(0)

  useEffect(() => {
    const tick = () => setNow(new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }))
    tick()
    const id = setInterval(tick, 10_000)
    return () => clearInterval(id)
  }, [])
  // Drift the layout a few pixels so static edges don't mark the panel.
  useEffect(() => {
    const id = setInterval(() => setOrbit(orbitOffset(Date.now())), 30_000)
    return () => clearInterval(id)
  }, [])
  useEffect(() => {
    if (authFailed) return
    const id = setInterval(() => authFailedExternal && setAuthFailed(true), 1_000)
    return () => clearInterval(id)
  }, [authFailed])

  // Keep the panel awake: the Screen Wake Lock API maps to Wayland idle-inhibit
  // under labwc, so the touchscreen never blanks while the kiosk is showing.
  // Re-acquire when the tab regains visibility (locks drop on hide).
  useEffect(() => {
    const nav = navigator as Navigator & {
      wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> }
    }
    if (!nav.wakeLock) return
    let lock: { release(): Promise<void> } | null = null
    const acquire = async () => {
      try {
        if (document.visibilityState === 'visible') lock = await nav.wakeLock!.request('screen')
      } catch {
        /* wake lock unavailable — non-fatal */
      }
    }
    void acquire()
    document.addEventListener('visibilitychange', acquire)
    return () => {
      document.removeEventListener('visibilitychange', acquire)
      void lock?.release()
    }
  }, [])

  const active = useMemo(() => (zoom ? DETAILS[zoom] : null), [zoom])

  if (authFailed) {
    return <KioskAuthFailed onRecovered={() => setAuthFailed(false)} />
  }

  return (
    <div
      className="kroot relative flex h-screen w-screen flex-col overflow-hidden"
      style={{ color: C.ink, background: C.rootGradient }}
    >
      <div
        className="kgrid-tex pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          backgroundImage:
            `linear-gradient(${C.gridLine} 1px,transparent 1px),` +
            `linear-gradient(90deg,${C.gridLine} 1px,transparent 1px)`,
        }}
      />
      <div
        className="relative flex min-h-0 flex-1 flex-col"
        style={{ transform: `translate3d(${orbit.x}px, ${orbit.y}px, 0)`, transition: 'transform 3s ease-in-out' }}
      >
        <StatusStrip
          now={now}
          onAlerts={() => setZoom('alerts')}
          mode={mode}
          onToggleTheme={onToggleTheme}
          onCare={() => setCareUntil(Date.now() + CLEAN_MS)}
        />
        <div
          className="relative grid min-h-0 flex-1 gap-3 p-3"
          style={{ gridTemplateColumns: '244px 1fr 1fr 320px', gridTemplateRows: 'minmax(0,1fr)' }}
        >
          <div className="kin min-h-0" style={{ animationDelay: '0ms' }}><PowerTile onTap={() => setZoom('power')} /></div>
          <div className="kin min-h-0" style={{ animationDelay: '70ms' }}><GpuTile onTap={() => setZoom('gpu')} /></div>
          <div className="kin min-h-0" style={{ animationDelay: '140ms' }}><NodesTile onTap={() => setZoom('nodes')} /></div>
          <div className="kin flex min-h-0 flex-col gap-3" style={{ animationDelay: '210ms' }}>
            <div className="min-h-0 flex-[3]"><NetworkTile onTap={() => setZoom('network')} /></div>
            <div className="min-h-0 flex-[2]"><StacksTile onTap={() => setZoom('stacks')} /></div>
          </div>
        </div>
      </div>
      {active && (
        <DetailShell title={active.title} onClose={() => setZoom(null)}>
          <active.Detail />
        </DetailShell>
      )}
      <ScreenCare manualUntil={careUntil} onStopManual={() => setCareUntil(0)} />
      <AlertNotifier />
    </div>
  )
}

export function KioskPage() {
  const [ready, setReady] = useState(false)
  const [mode, setMode] = useState<KioskMode>('dark')

  useEffect(() => {
    initKioskKey()
    // Restore the operator's choice before first paint of the tree, so the
    // panel never flashes the wrong theme after the auto-reload on a new build.
    const stored = readStoredMode()
    applyKioskTheme(stored, false)
    setMode(stored)
    setReady(true)
  }, [])

  const toggleTheme = () => {
    const next: KioskMode = mode === 'dark' ? 'light' : 'dark'
    applyKioskTheme(next)
    setMode(next)
  }

  if (!ready) return <div className="kroot h-screen w-screen" />
  if (!getKioskKey()) {
    return (
      <>
        <style>{KIOSK_CSS}</style>
        <div className="kroot flex h-screen w-screen flex-col items-center justify-center"
      style={{ color: C.ink, background: C.rootGradient }}>
          <p className="text-2xl font-bold">Kiosk non configuré</p>
          <p className="mt-2 text-sm" style={{ color: C.dim }}>Ouvrir l'URL avec <code className="kmono">#k=&lt;clé&gt;</code></p>
        </div>
      </>
    )
  }
  return (
    <QueryClientProvider client={kioskQC}>
      <style>{KIOSK_CSS}</style>
      {/* `key` forces a remount on theme change: the palette is a mutated
          module object, so React cannot otherwise know the colours changed.
          The QueryClient sits above, so the data cache survives the swap. */}
      <KioskInner key={mode} mode={mode} onToggleTheme={toggleTheme} />
    </QueryClientProvider>
  )
}
